<template>
  <div class="LKPublicationsCardRecommendationRpshV3MassGeneration">
    <div class="LKPublicationsCardRecommendationRpshV3MassGeneration__title">
      Генерация профилей школ
    </div>

    <div class="LKPublicationsCardRecommendationRpshV3MassGeneration__description">
      Загрузите CSV с логинами, запустите генерацию и отслеживайте статус выполнения в таблице.
    </div>

    <div class="LKPublicationsCardRecommendationRpshV3MassGeneration__controls">
      <BFileInput
        v-model="file"
        accept=".csv,text/csv"
        label="CSV файл с логинами..."
        small-chips
        show-size
        hide-details="auto"
        class="LKPublicationsCardRecommendationRpshV3MassGeneration__file"
        @change="onFileChange"
      />

      <div class="LKPublicationsCardRecommendationRpshV3MassGeneration__run">
        <v-checkbox
          v-model="forced"
          label="полный перерасчет"
          dense
          hide-details
          class="LKPublicationsCardRecommendationRpshV3MassGeneration__checkbox"
        />

        <v-btn
          color="primary"
          outlined
          small
          :loading="isUploading"
          :disabled="!canUpload"
          class="LKPublicationsCardRecommendationRpshV3MassGeneration__button"
          @click="onUpload"
        >
          Запустить генерацию
        </v-btn>
      </div>
    </div>

    <div class="LKPublicationsCardRecommendationRpshV3MassGeneration__summary">
      <span>Обработано: {{ done }} из {{ total }}</span>
      <span>Прогресс: {{ progress }}%</span>
    </div>
    <v-progress-linear
      :value="progress"
      color="primary"
      class="LKPublicationsCardRecommendationRpshV3MassGeneration__progress"
    />

    <div class="LKPublicationsCardRecommendationRpshV3MassGeneration__filters">
      <v-text-field
        v-model="schoolLoginFilter"
        label="Логин школы"
        clearable
        dense
        hide-details
      />
      <v-select
        v-model="statusFilter"
        :items="statusOptions"
        label="Статус"
        item-text="text"
        item-value="value"
        clearable
        dense
        hide-details
      />
    </div>

    <v-data-table
      :headers="headers"
      :items="filteredItems"
      :loading="isLoading"
      :items-per-page="5"
      :footer-props="footerProps"
      no-data-text="..."
      dense
      class="LKPublicationsCardRecommendationRpshV3MassGeneration__table"
    >
      <template #item.status="{ item }">
        {{ statusLabels[item.status] || item.status }}
      </template>

      <template #item.error_message="{ item }">
        <span class="error--text">{{ item.error_message || '—' }}</span>
      </template>

      <template #item.actions="{ item }">
        <BTooltipButton
          v-if="item.status === 'done'"
          small
          depressed
          bottom
          icon="mdi-download"
          tooltip="Скачать"
          class-button="LKPublicationsCardRecommendationRpshV3MassGeneration__action"
          :loading="downloadLoadingLogin === item.school_login"
          @click="onDownload(item.school_login)"
        />
      </template>
    </v-data-table>
  </div>
</template>

<script>
import BFileInput from '@/components/forms/BFileInput.vue'
import BTooltipButton from '@/components/common/BTooltipButton.vue'
import { LkRecomendationApi } from '@/api/resources'
import { downloadLinkByGet } from '@/helpers/downloadBlob'

const POLL_INTERVAL = 4000

export default {
  name: 'LKPublicationsCardRecommendationRpshV3MassGeneration',
  components: {
    BFileInput,
    BTooltipButton,
  },
  props: {
    publicationId: {
      type: Number,
      required: true,
    },
  },
  data: () => ({
    file: null,
    forced: false,
    schoolLoginFilter: '',
    statusFilter: null,
    statusOptions: [
      { text: 'В работе', value: 'in_progress' },
      { text: 'Недостаточно участников', value: 'not_ready' },
      { text: 'Готов', value: 'done' },
      { text: 'Ошибка', value: 'error' },
    ],
    statusLabels: {
      in_progress: 'В работе',
      not_ready: 'Недостаточно участников',
      done: 'Готово',
      error: 'Ошибка',
    },
    items: [],
    total: 0,
    done: 0,
    progress: 0,
    isUploading: false,
    isLoading: false,
    downloadLoadingLogin: null,
    pollingTimerId: null,
    footerProps: {
      itemsPerPageText: 'Строк на странице',
    },
    headers: [
      { text: 'Логин школы', value: 'school_login' },
      { text: 'Статус', value: 'status', sortable: false },
      { text: 'Ошибка', value: 'error_message', sortable: false },
      {
        text: '', value: 'actions', sortable: false, width: 130,
      },
    ],
  }),
  computed: {
    canUpload() {
      return Boolean(this.file && !this.isUploading)
    },
    filteredItems() {
      const login = this.schoolLoginFilter.trim().toLowerCase()
      return this.items.filter((item) => (
        (!login || item.school_login.toLowerCase().includes(login))
        && (!this.statusFilter || item.status === this.statusFilter)
      ))
    },
  },
  mounted() {
    this.getStatus()
    this.startPolling()
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    onFileChange(file) {
      this.file = file
    },
    async onUpload() {
      const api = new LkRecomendationApi()
      this.isUploading = true

      try {
        const result = await api.uploadRpshV3MassGeneration(
          this.publicationId,
          this.file,
          this.forced,
        )
        this.file = null
        this.$notice.info(`Запущена генерация профилей: ${result.started} из ${result.total}`)
        await this.getStatus()
        this.startPolling()
      } catch (e) {
        this.$notice.errors(e)
      } finally {
        this.isUploading = false
      }
    },
    startPolling() {
      this.stopPolling()
      this.pollingTimerId = setInterval(this.getStatus, POLL_INTERVAL)
    },
    stopPolling() {
      if (this.pollingTimerId) clearInterval(this.pollingTimerId)
      this.pollingTimerId = null
    },
    async getStatus() {
      if (this.isLoading) return

      const api = new LkRecomendationApi()
      this.isLoading = true

      try {
        const result = await api.getRpshV3MassGenerationStatus(this.publicationId)
        this.items = result.items || []
        this.total = result.total || this.total
        this.done = result.done || 0
        this.progress = result.progress || 0
      } catch (e) {
        this.$notice.errors(e)
      } finally {
        this.isLoading = false
      }
    },
    async onDownload(schoolLogin) {
      const api = new LkRecomendationApi()
      this.downloadLoadingLogin = schoolLogin

      try {
        await downloadLinkByGet(
          api.getRpshV3MassGenerationDownloadUrl(this.publicationId, schoolLogin),
        )
      } catch (e) {
        this.$notice.errors(e)
      } finally {
        this.downloadLoadingLogin = null
      }
    },
  },
}
</script>

<style lang="scss">
.LKPublicationsCardRecommendationRpshV3MassGeneration {
  margin-bottom: 14px;
  padding: 12px;
  border: 1px solid rgba(0, 0, 0, 0.10);
  border-radius: 4px;

  &__title {
    margin-bottom: 4px;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.3;
  }

  &__description {
    max-width: 760px;
    margin-bottom: 12px;
    color: rgba(0, 0, 0, 0.62);
    font-size: 12px;
    line-height: 1.45;
  }

  &__controls {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 12px;
    align-items: flex-start;
  }

  &__file {
    flex: 1 1 320px;
    max-width: 420px;
  }

  &__run {
    display: flex;
    flex: 0 1 auto;
    gap: 12px;
    align-items: center;
    min-height: 40px;
  }

  &__checkbox {
    flex: 0 0 auto;
    margin-top: 0 !important;
    padding-top: 0 !important;
  }

  &__button {
    min-height: 40px;
    margin-top: 0 !important;
  }

  &__summary {
    display: flex;
    gap: 18px;
    margin-top: 14px;
    font-size: 13px;
  }

  &__progress {
    margin: 8px 0 12px;
  }

  &__filters {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin: 8px 0;

    .v-input {
      flex: 1 1 220px;
      max-width: 320px;
    }
  }

  &__table {
    margin-top: 8px;

    .v-data-table__wrapper {
      overflow-x: auto;
    }
  }

  &__action {
    min-width: 28px !important;
    width: 28px;
    height: 28px !important;
    margin-left: 4px;
  }
}
</style>
